import React, { useEffect, useState } from 'react';
import { Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useBranding } from '../../contexts/BrandingContext';
import Routes from '../../routes/Routes';
import { useOutlookPromo } from '../../hooks/useOutlookPromo';
import { trackUiButtonEventAsync } from '../../services/api/uiButtonStatsService';
import './admin-onboarding.css';

/**
 * Étape funnel admin : après invitation collaborateurs, avant referral survey.
 */
const OutlookPromoOnboardingView = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { branding } = useBranding();
    const { refresh, ack, installUrl, eligible, loading, data } = useOutlookPromo({ autoFetch: true });
    const [ready, setReady] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const state = await refresh();
            if (cancelled) return;
            const pendingOnboarding = state?.pending?.includes('onboarding');
            if (!state?.eligible || !pendingOnboarding) {
                navigate(Routes.REFERRAL, { replace: true });
                return;
            }
            await ack('onboarding', 'shown');
            if (!cancelled) setReady(true);
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const goNext = () => navigate(Routes.REFERRAL, { replace: true });

    const handleCta = async () => {
        await trackUiButtonEventAsync('outlook_promo.onboarding.cta');
        await ack('onboarding', 'cta');
        window.open(installUrl, '_blank', 'noopener,noreferrer');
        goNext();
    };

    const handleLater = async () => {
        await trackUiButtonEventAsync('outlook_promo.onboarding.dismiss');
        await ack('onboarding', 'dismiss');
        goNext();
    };

    if (loading && !ready) {
        return (
            <main className="admin-onboarding">
                <section className="admin-onboarding__stage">
                    <div className="admin-onboarding__panel">
                        <p className="text-center text-muted">{t('words.loading', 'Loading…')}</p>
                    </div>
                </section>
            </main>
        );
    }

    if (!ready || !eligible) {
        return null;
    }

    return (
        <main className="admin-onboarding">
            <header className="admin-onboarding__header">
                <span className="admin-onboarding__brand">{branding.appName || 'Famla'}</span>
                <button type="button" className="admin-onboarding__skip" onClick={handleLater}>
                    {t('outlookPromo.skip', 'Plus tard')}
                </button>
            </header>

            <section className="admin-onboarding__stage">
                <div className="admin-onboarding__panel">
                    <div className="admin-onboarding__welcome">
                        <span className="admin-onboarding__mark" aria-hidden="true">
                            <Mail size={28} />
                        </span>
                        <h1>{t('outlookPromo.onboardingTitle', 'Travaillez aussi dans Outlook')}</h1>
                        <p>
                            {t(
                                'outlookPromo.onboardingDescription',
                                "Installez l'extension Famla pour cartographier et collaborer sans quitter votre boîte mail.",
                            )}
                        </p>
                        <button type="button" className="admin-onboarding__primary" onClick={handleCta}>
                            {t('outlookPromo.installCta', "Installer l'extension Outlook")}
                        </button>
                        <button
                            type="button"
                            className="admin-onboarding__skip mt-3"
                            style={{ position: 'static' }}
                            onClick={handleLater}
                        >
                            {t('outlookPromo.later', 'Continuer sans installer')}
                        </button>
                        {data?.install_url ? (
                            <small className="d-block mt-3 text-muted">{data.install_url}</small>
                        ) : null}
                    </div>
                </div>
            </section>
        </main>
    );
};

export default OutlookPromoOnboardingView;
